import { z } from 'zod';
import type { ManualAccess, ManualAccessWrite, RoleAccess } from '@/types/oscar';

/**
 * Forms and display rules for the branch relationship screen (migration 128;
 * Data-Model §17). The server re-checks everything; these exist so the admin
 * sees the reason before a round trip.
 */

// ── Role access (oscar.DepartmentModule) ──────────────────────────────────────

export const geographyLabel: Record<string, string> = { B: 'Branch', R: 'Region', N: 'National' };

/**
 * What a role mapping actually yields, in words — the rules of
 * oscar.vPersonBranchScope (a)-(d). An N- role on the geographic rule yields
 * NOTHING, which is easy to set up by mistake, so it says so.
 */
export function describeRoleScope(geographicCode: string, scopeOverride: string | null): string {
  if (scopeOverride === 'Company') return 'Company — every branch';
  switch (geographicCode) {
    case 'B': return "Their location's branch, plus their cost centre";
    case 'R': return 'Every branch in their region, plus their cost centre';
    case 'N': return 'Nothing — a national role needs Company scope or manual rows';
    default: return 'Geographic rule';
  }
}

/** True when the mapping is live but can never yield a row (N- role, geographic rule). */
export function yieldsNothing(row: Pick<RoleAccess, 'GeographicCode' | 'ScopeOverride' | 'IsDeleted'>): boolean {
  return !row.IsDeleted && row.ScopeOverride === null && row.GeographicCode === 'N';
}

// ── Manual access (oscar.PersonBranchRelationship) ────────────────────────────

/** What a manual grant is FOR — one row of the coverage panel. */
export type GrantTarget =
  | { ScopeType: 'Company' }
  | { ScopeType: 'Region'; RegionId: number; RegionDesc: string }
  | { ScopeType: 'Branch'; BranchCode: string }
  /** A cost-centre code typed in, because it is not an Oscar location (1102 IT). */
  | { ScopeType: 'Branch'; BranchCode: null };

/**
 * The grant dialog: why, and for how long. The scope comes from the row that
 * was clicked, so the only shape rule left to check here is the typed code.
 */
export const grantSchema = z
  .object({
    CustomCode: z.boolean(),
    BranchCode: z.string().trim().max(20, 'At most 20 characters'),
    Reason: z.string().trim().min(1, 'Say why this person has the access').max(400, 'At most 400 characters'),
    /** yyyy-mm-dd from <input type="date">, '' for open. */
    ValidFrom: z.string(),
    ValidTo: z.string(),
  })
  .superRefine((d, ctx) => {
    if (d.CustomCode && d.BranchCode.trim() === '')
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['BranchCode'], message: 'Enter the branch or cost-centre code' });
    // CK_PersonBranchRelationship_Dates (inclusive). ISO dates compare as strings.
    if (d.ValidFrom && d.ValidTo && d.ValidTo < d.ValidFrom)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ValidTo'], message: 'Must be on or after Valid from' });
  });
export type GrantFormData = z.infer<typeof grantSchema>;

export const emptyGrant = (customCode: boolean): GrantFormData => ({
  CustomCode: customCode, BranchCode: '', Reason: '', ValidFrom: '', ValidTo: '',
});

/**
 * Dialog + clicked row → API. Exactly the column the scope needs, everything
 * else null (CK_PersonBranchRelationship_Shape).
 */
export function toGrantPayload(personId: number, moduleKey: string, target: GrantTarget, d: GrantFormData): ManualAccessWrite {
  return {
    PersonId: personId,
    ModuleKey: moduleKey,
    ScopeType: target.ScopeType,
    BranchCode: target.ScopeType === 'Branch' ? (target.BranchCode ?? d.BranchCode.trim()) : null,
    RegionId: target.ScopeType === 'Region' ? target.RegionId : null,
    Reason: d.Reason.trim(),
    ValidFrom: d.ValidFrom || null,
    ValidTo: d.ValidTo || null,
  };
}

/** The coverage-panel key of a live row, matched against the panel's targets. */
export function scopeKey(row: Pick<ManualAccess, 'ScopeType' | 'BranchCode' | 'RegionId'>): string {
  if (row.ScopeType === 'Company') return 'company';
  if (row.ScopeType === 'Region') return `region:${row.RegionId}`;
  return `branch:${(row.BranchCode ?? '').trim()}`;
}

/** Today as yyyy-mm-dd in local time (the view compares in Sydney time). */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export type ManualStatus = 'Live' | 'Not yet' | 'Expired' | 'Withdrawn';

/** Whether a manual row currently counts — mirrors the view's ValidFrom..ValidTo filter. */
export function manualStatus(
  row: Pick<ManualAccess, 'IsDeleted' | 'ValidFrom' | 'ValidTo'>,
  today: string = todayIso(),
): ManualStatus {
  if (row.IsDeleted) return 'Withdrawn';
  const from = row.ValidFrom?.slice(0, 10);
  const to = row.ValidTo?.slice(0, 10);
  if (from && from > today) return 'Not yet';
  if (to && to < today) return 'Expired';
  return 'Live';
}

/** "Branch 1102", "Region Queensland", "Company". */
export function describeManualScope(row: Pick<ManualAccess, 'ScopeType' | 'BranchCode' | 'RegionDesc' | 'RegionId'>): string {
  if (row.ScopeType === 'Branch') return `Branch ${row.BranchCode ?? ''}`.trim();
  if (row.ScopeType === 'Region') return `Region ${row.RegionDesc ?? `#${row.RegionId ?? ''}`}`;
  return 'Company — every branch';
}

export const sourceLabel: Record<string, string> = {
  Role: 'Role',
  CostCentre: 'Cost centre',
  Manual: 'Manual',
};
